import React, { useCallback, useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FilterSidebarFormData } from "@/modules/hub/features/ResourceLibraryV2/hooks/FilterSidebarFormSchema";
import { ProcessWheelFacet } from "@/modules/hub/features/ResourceLibraryV2/api/getResourceLibratyMetadata/types";
import {
  WHEEL_STAGES,
  WHEEL_WEDGES,
} from "@/modules/hub/features/ResourceLibraryV2/components/FilterSidebar/processWheelSegments";
import "./ProcessWheelModal.css";

interface ProcessWheelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<FilterSidebarFormData>;
  processWheels: ProcessWheelFacet[];
}

// Bottom phase pills.
const PHASE_PILLS: {
  id: string;
  x: number;
  fill: string;
  textFill: string;
  textX: number;
  label: string;
}[] = [
  {
    id: "build",
    x: 114,
    fill: "#1C1349",
    textFill: "#fff",
    textX: 204.5,
    label: "Build & Grow",
  },
  {
    id: "contact",
    x: 311,
    fill: "#C8102E",
    textFill: "#fff",
    textX: 401.5,
    label: "Client Contact",
  },
  {
    id: "execute",
    x: 508,
    fill: "#93A9AD",
    textFill: "#1C1349",
    textX: 598.5,
    label: "Execute Calls",
  },
  {
    id: "analyze",
    x: 705,
    fill: "#F8C44E",
    textFill: "#1C1349",
    textX: 795.5,
    label: "Analyze Results",
  },
];

// Curved labels that follow the arc <path>s defined in <defs>.
const ARC_LABELS: { arc: string; text: string }[] = [
  { arc: "arc_culture_0", text: "Build Your" },
  { arc: "arc_culture_1", text: "Culture & Lead" },
  { arc: "arc_strategy_0", text: "Strategy &" },
  { arc: "arc_strategy_1", text: "Planning" },
  { arc: "arc_buildteam_0", text: "Build" },
  { arc: "arc_buildteam_1", text: "Your" },
  { arc: "arc_buildteam_2", text: "Team" },
  { arc: "arc_mgmt_0", text: "Management" },
  { arc: "arc_mgmt_1", text: "Development" },
  { arc: "arc_develop_0", text: "Develop" },
  { arc: "arc_develop_1", text: "Your" },
  { arc: "arc_develop_2", text: "Team" },
  { arc: "arc_scale_0", text: "Scale" },
  { arc: "arc_scale_1", text: "Your" },
  { arc: "arc_scale_2", text: "Business" },
];

// Straight (multi-line) wedge labels.
const WEDGE_LABELS: {
  x: number;
  y: number;
  fill: string;
  lines: { text: string; dy: number }[];
}[] = [
  {
    x: 500,
    y: 312,
    fill: "#fff",
    lines: [
      { text: "Book the", dy: -5.4 },
      { text: "Call", dy: 25 },
    ],
  },
  {
    x: 614.42,
    y: 353.64,
    fill: "#fff",
    lines: [
      { text: "Schedule", dy: -5.4 },
      { text: "& Dispatch", dy: 25 },
    ],
  },
  {
    x: 675.3,
    y: 459.09,
    fill: "#1C1349",
    lines: [
      { text: "Run the", dy: -5.4 },
      { text: "Call", dy: 25 },
    ],
  },
  {
    x: 654.15,
    y: 579,
    fill: "#1C1349",
    lines: [
      { text: "Debrief &", dy: -5.4 },
      { text: "Close Out", dy: 25 },
    ],
  },
  {
    x: 560.88,
    y: 657.27,
    fill: "#1C1349",
    lines: [
      { text: "Restock &", dy: -5.4 },
      { text: "Reconcile", dy: 25 },
    ],
  },
  {
    x: 439.12,
    y: 657.27,
    fill: "#1C1349",
    lines: [
      { text: "Report", dy: -5.4 },
      { text: "Financials", dy: 25 },
    ],
  },
  {
    x: 345.85,
    y: 579,
    fill: "#1C1349",
    lines: [
      { text: "Review", dy: -5.4 },
      { text: "KPIs", dy: 25 },
    ],
  },
  {
    x: 324.7,
    y: 459.09,
    fill: "#1C1349",
    lines: [
      { text: "Inspect", dy: -17.9 },
      { text: "What You", dy: 25 },
      { text: "Expect", dy: 25 },
    ],
  },
  {
    x: 385.58,
    y: 353.64,
    fill: "#fff",
    lines: [
      { text: "Generate", dy: -5.4 },
      { text: "Demand", dy: 25 },
    ],
  },
];

const ProcessWheelModal: React.FC<ProcessWheelModalProps> = ({
  open,
  onOpenChange,
  form,
  processWheels,
}) => {
  const [closing, setClosing] = useState(false);

  // Keep internal closing state in sync if the modal is closed externally.
  useEffect(() => {
    if (!open) setClosing(false);
  }, [open]);

  // Play the spin/fade-out animation, then actually close.
  const requestClose = useCallback(() => {
    if (closing) return;
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      onOpenChange(false);
      return;
    }
    setClosing(true);
  }, [closing, onOpenChange]);

  const handleAnimationEnd = (event: React.AnimationEvent<HTMLDivElement>) => {
    if (closing && event.animationName === "rlw-spin-out") {
      setClosing(false);
      onOpenChange(false);
    }
  };

  const handleSelectWheel = (wheelName: string) => {
    const facet = processWheels.find((wheel) => wheel.name === wheelName);
    if (!facet) return;

    const current = form.getValues("processWheels") ?? [];
    const isSelected = current.some((wheel) => wheel.id === facet.id);
    const next = isSelected ? [] : [{ id: facet.id, name: facet.name }];

    form.setValue("processWheels", next, { shouldDirty: true });
    requestClose();
  };

  // Shared props that make a wheel slice a keyboard-accessible filter control.
  const sliceProps = (wheelName: string, delay: number) => ({
    "aria-label": `Filter by ${wheelName}`,
    onClick: () => handleSelectWheel(wheelName),
    onKeyDown: (event: React.KeyboardEvent<SVGPathElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleSelectWheel(wheelName);
      }
    },
    role: "button",
    style: { cursor: "pointer", animationDelay: `${delay}s` },
    tabIndex: 0,
  });

  return (
    <Dialog
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
      open={open}
    >
      <DialogContent
        className="max-w-[760px] overflow-visible border-0 bg-transparent dark:bg-transparent p-0 shadow-none"
        hideCloseButton
      >
        <div
          aria-label="Filter by Process Wheel"
          className={`relative w-full${
            closing
              ? " origin-center animate-[rlw-spin-out_0.5s_ease-in_forwards]"
              : ""
          }`}
          onAnimationEnd={handleAnimationEnd}
        >
          <button
            aria-label="Close"
            className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white text-[1.1rem] leading-none text-[#1C1349] shadow-[0_2px_8px_rgba(0,0,0,0.25)] transition-colors hover:bg-[#f1f1f1]"
            onClick={requestClose}
            type="button"
          >
            ✕
          </button>

          <svg
            aria-label="Process wheel filter"
            className="block h-auto w-full"
            role="group"
            style={{
              fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
            }}
            viewBox="0 0 1000 1052"
            width="100%"
          >
            <defs>
              <path
                d="M 74.56 414.98 A 432 432 0 0 1 925.44 414.98"
                fill="none"
                id="titleArc"
              />
              <path
                d="M 335.16 192.63 A 340 340 0 0 1 664.84 192.63"
                fill="none"
                id="arc_culture_0"
              />
              <path
                d="M 354.56 227.61 A 300 300 0 0 1 645.44 227.61"
                fill="none"
                id="arc_culture_1"
              />
              <path
                d="M 675.11 198.56 A 340 340 0 0 1 839.95 484.07"
                fill="none"
                id="arc_strategy_0"
              />
              <path
                d="M 654.51 232.85 A 300 300 0 0 1 799.95 484.76"
                fill="none"
                id="arc_strategy_1"
              />
              <path
                d="M 645.24 731.72 A 282 282 0 0 0 781.96 494.92"
                fill="none"
                id="arc_buildteam_0"
              />
              <path
                d="M 663.78 762.58 A 318 318 0 0 0 817.95 495.55"
                fill="none"
                id="arc_buildteam_1"
              />
              <path
                d="M 682.32 793.44 A 354 354 0 0 0 853.95 496.18"
                fill="none"
                id="arc_buildteam_2"
              />
              <path
                d="M 354.56 752.39 A 300 300 0 0 0 645.44 752.39"
                fill="none"
                id="arc_mgmt_0"
              />
              <path
                d="M 335.16 787.37 A 340 340 0 0 0 664.84 787.37"
                fill="none"
                id="arc_mgmt_1"
              />
              <path
                d="M 218.04 494.92 A 282 282 0 0 0 354.76 731.72"
                fill="none"
                id="arc_develop_0"
              />
              <path
                d="M 182.05 495.55 A 318 318 0 0 0 336.22 762.58"
                fill="none"
                id="arc_develop_1"
              />
              <path
                d="M 146.05 496.18 A 354 354 0 0 0 317.68 793.44"
                fill="none"
                id="arc_develop_2"
              />
              <path
                d="M 146.05 483.82 A 354 354 0 0 1 317.68 186.56"
                fill="none"
                id="arc_scale_0"
              />
              <path
                d="M 182.05 484.45 A 318 318 0 0 1 336.22 217.42"
                fill="none"
                id="arc_scale_1"
              />
              <path
                d="M 218.04 485.08 A 282 282 0 0 1 354.76 248.28"
                fill="none"
                id="arc_scale_2"
              />
            </defs>

            {/* Bottom phase-pill tray */}
            <rect
              fill="#15727A"
              height="94"
              rx="47"
              width="816"
              x="92"
              y="948"
            />
            {PHASE_PILLS.map((pill) => {
              const rect = (
                <rect
                  fill={pill.fill}
                  height="50"
                  rx="25"
                  stroke="none"
                  strokeWidth="0"
                  width="181"
                  x={pill.x}
                  y="970"
                />
              );
              const text = (
                <text
                  dominantBaseline="middle"
                  fill={pill.textFill}
                  fontSize="21"
                  fontWeight="700"
                  pointerEvents="none"
                  textAnchor="middle"
                  x={pill.textX}
                  y="996"
                >
                  {pill.label}
                </text>
              );
              return (
                <g data-testid={`wheel-phase-${pill.id}`} key={pill.id}>
                  {rect}
                  {text}
                </g>
              );
            })}

            {/* Main wheel disc + title */}
            <circle cx="500" cy="490" fill="#15727A" r="470" />
            <text
              fill="#fff"
              fontSize="33"
              fontWeight="700"
              pointerEvents="none"
            >
              <textPath href="#titleArc" startOffset="50%" textAnchor="middle">
                The Process to Build a Successful Home Service Business
              </textPath>
            </text>

            {/* Outer ring — business stages */}
            {WHEEL_STAGES.map((stage) => (
              <path
                className="animate-[rlw-slice-in_0.4s_ease-out_both] outline-none transition-[filter] hover:brightness-[1.08] focus-visible:brightness-[1.08] motion-reduce:animate-none"
                d={stage.d}
                data-testid={`wheel-stage-${stage.id}`}
                fill={stage.fill}
                key={stage.id}
                stroke="none"
                strokeWidth="0"
                {...sliceProps(stage.wheelName, stage.delay)}
              />
            ))}

            {/* Inner ring — process steps */}
            {WHEEL_WEDGES.map((wedge) => (
              <path
                className="animate-[rlw-slice-in_0.4s_ease-out_both] outline-none transition-[filter] hover:brightness-[1.08] focus-visible:brightness-[1.08] motion-reduce:animate-none"
                d={wedge.d}
                data-testid={`wheel-wedge-${wedge.id}`}
                fill={wedge.fill}
                key={wedge.id}
                stroke="none"
                strokeWidth="0"
                {...sliceProps(wedge.wheelName, wedge.delay)}
              />
            ))}

            {/* Hub + logo */}
            <circle
              cx="500"
              cy="490"
              fill="#FCF6EA"
              pointerEvents="none"
              r="90"
            />
            <g
              pointerEvents="none"
              transform="translate(500,490) scale(0.2344) translate(-214,-292)"
            >
              <path
                d="M209.62,548.06,378.84,378.84a123.53,123.53,0,0,0,14.53-17.54c.33-.48.67-.95,1-1.43.44-.66.85-1.34,1.27-2,.72-1.13,1.44-2.25,2.11-3.39l.21-.36a122.88,122.88,0,0,0-19.11-149L209.62,35.91A122.79,122.79,0,0,0,36,209.56L75.5,249.1a23.89,23.89,0,0,0,33.78-33.78L69.74,175.78A75,75,0,1,1,175.84,69.69l100.6,100.6a122.2,122.2,0,0,0-71.25,34.89L36,374.41A122.79,122.79,0,1,0,209.62,548.06ZM69.74,514.29a75,75,0,0,1,0-106.1L239,239a75,75,0,1,1,0,106,23.89,23.89,0,1,0-33.78,33.78,122.23,122.23,0,0,0,71.26,34.89L175.84,514.29a75,75,0,0,1-106.1,0Z"
                fill="#C8102E"
              />
            </g>

            {/* Curved stage labels */}
            {ARC_LABELS.map((label) => (
              <text
                fill="#fff"
                fontSize="27"
                fontWeight="700"
                key={label.arc}
                pointerEvents="none"
              >
                <textPath
                  dominantBaseline="middle"
                  href={`#${label.arc}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {label.text}
                </textPath>
              </text>
            ))}

            {/* Straight wedge labels */}
            {WEDGE_LABELS.map((label) => (
              <text
                fill={label.fill}
                fontSize="21"
                fontWeight="700"
                key={`${label.x}-${label.y}`}
                pointerEvents="none"
                textAnchor="middle"
                x={label.x}
                y={label.y}
              >
                {label.lines.map((line, i) => (
                  <tspan dy={line.dy} key={i} x={label.x}>
                    {line.text}
                  </tspan>
                ))}
              </text>
            ))}
          </svg>

          <button
            className="mx-auto mt-3 block rounded-full bg-[#f1eee6] px-[1.1rem] py-[0.4rem] text-[0.8rem] font-semibold text-[#1C1349] transition-colors hover:bg-[#e4e0d4]"
            onClick={requestClose}
            type="button"
          >
            Show all resources
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessWheelModal;
